"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { FiStar, FiZap } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

type PointAnimation = {
  id: string;
  points: number;
  startX: number;
  startY: number;
  type: "fp" | "aura";
};

type PointsContextType = {
  triggerAnimation: (points: number, startX: number, startY: number, type?: "fp" | "aura") => void;
  setFPTargetRef: (ref: HTMLElement | null) => void;
  setMobileTargetRef: (ref: HTMLElement | null) => void;
  setProgressBarRef: (ref: HTMLElement | null) => void;
  updatePoints: (newPoints: number) => void;
  subtractPoints: (points: number) => void;
  currentPoints: number;
  refreshSidebar: () => void;
  sidebarRefreshKey: number;
};

const PointsContext = createContext<PointsContextType | null>(null);

export function PointsProvider({ children }: { children: React.ReactNode }) {
  const [animations, setAnimations] = useState<PointAnimation[]>([]);
  const [currentPoints, setCurrentPoints] = useState(0);
  const [fpTargetElement, setFPTargetElement] = useState<HTMLElement | null>(null);
  const [mobileTargetElement, setMobileTargetElement] = useState<HTMLElement | null>(null);
  const [progressBarElement, setProgressBarElement] = useState<HTMLElement | null>(null);
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

  const setFPTargetRef = useCallback((ref: HTMLElement | null) => {
    setFPTargetElement(ref);
  }, []);

  const setMobileTargetRef = useCallback((ref: HTMLElement | null) => {
    setMobileTargetElement(ref);
  }, []);

  const setProgressBarRef = useCallback((ref: HTMLElement | null) => {
    setProgressBarElement(ref);
  }, []);

  const updatePoints = useCallback((newPoints: number) => {
    setCurrentPoints(newPoints);
  }, []);

  const subtractPoints = useCallback((points: number) => {
    setCurrentPoints(prev => Math.max(0, prev - points));
  }, []);

  const refreshSidebar = useCallback(() => {
    setSidebarRefreshKey(prev => prev + 1);
  }, []);

  // `points` can be negative — that renders a red "-X FP" pill flying
  // away (loss) instead of the lime "+X FP" pill flying toward the FP
  // target. The currentPoints state always settles to `prev + points`,
  // so a -1 pill arrival also drops the visible balance by 1. Used by
  // the post-delete flow (penalty refund) and by the /toasts-ff debug
  // page to preview both directions.
  const triggerAnimation = useCallback((points: number, startX: number, startY: number, type: "fp" | "aura" = "fp") => {
    const id = `${Date.now()}-${Math.random()}`;
    setAnimations(prev => [...prev, { id, points, startX, startY, type }]);

    // Remove animation after it completes and refresh sidebar
    setTimeout(() => {
      setAnimations(prev => prev.filter(a => a.id !== id));
      // Update points after animation (clamped at 0)
      setCurrentPoints(prev => Math.max(0, prev + points));
      // Trigger sidebar refresh to fetch new data
      refreshSidebar();
    }, 1800);
  }, [refreshSidebar]);

  const removeAnimation = useCallback((id: string) => {
    setAnimations(prev => prev.filter(a => a.id !== id));
  }, []);

  return (
    <PointsContext.Provider value={{
      triggerAnimation,
      setFPTargetRef,
      setMobileTargetRef,
      setProgressBarRef,
      updatePoints,
      subtractPoints,
      currentPoints,
      refreshSidebar,
      sidebarRefreshKey
    }}>
      {children}
      {/* Render floating animations */}
      {animations.map(anim => (
        <FloatingPoint
          key={anim.id}
          points={anim.points}
          startX={anim.startX}
          startY={anim.startY}
          fpTargetRef={fpTargetElement}
          mobileTargetRef={mobileTargetElement}
          progressBarRef={progressBarElement}
          type={anim.type}
          onComplete={() => removeAnimation(anim.id)}
        />
      ))}
    </PointsContext.Provider>
  );
}

export function usePoints() {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error("usePoints must be used within a PointsProvider");
  }
  return context;
}

/**
 * Floating point animation — fintech-grade XP feedback.
 *
 * Three-phase choreography (1.6s total):
 *   1. Birth (0-180ms): pill scales in from 0 with a tiny radial burst
 *      of 6 lime particles at the trigger site. Quick, punchy, signals
 *      "something just earned".
 *   2. Travel (180-1300ms): pill smoothly arcs toward the FP target
 *      (sidebar on desktop, header avatar on mobile) on a slight
 *      easing curve. Fades to 0.8 and scales to 0.65 as it approaches.
 *   3. Arrival (1300-1600ms): radial explosion at the target — 8
 *      micro-particles fan out, a soft lime ring expands and fades,
 *      and the target itself gets a brief scale pulse. No `animate-ping`
 *      hammering; clean spring physics via framer-m.
 *
 * Visual language: dark glass pill (bg-[#0a0a0b]/95, white text, lime
 * monument number), no gradient fill, no oversized glow shadow.
 * Reads as "premium dashboard event", not "crypto banner ad".
 */
function FloatingPoint({
  points,
  startX,
  startY,
  fpTargetRef,
  mobileTargetRef,
  progressBarRef,
  type,
  onComplete
}: {
  points: number;
  startX: number;
  startY: number;
  fpTargetRef: HTMLElement | null;
  mobileTargetRef: HTMLElement | null;
  progressBarRef: HTMLElement | null;
  type: "fp" | "aura";
  onComplete: () => void;
}) {
  const [target, setTarget] = useState<{ x: number; y: number; rect: DOMRect | null }>({
    x: startX,
    y: startY - 80,
    rect: null,
  });
  const [phase, setPhase] = useState<'birth' | 'travel' | 'arrival' | 'done'>('birth');

  // Memoize the radial particle vectors so they're stable per animation.
  // 8 particles at 45° increments with slight random jitter so back-to-back
  // triggers don't feel mechanically identical. Magnitude is intentionally
  // small (28-36px) — these are accents, not fireworks.
  const burstParticles = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        angle: (i / 6) * Math.PI * 2 + Math.random() * 0.3,
        distance: 22 + Math.random() * 12,
      })),
    [],
  );
  const arrivalParticles = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        angle: (i / 8) * Math.PI * 2 + Math.random() * 0.25,
        distance: 32 + Math.random() * 14,
      })),
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth < 1024;

    // Resolve the target at animation time, NOT at registration time.
    // Three-step lookup, in order of preference:
    //   1. Live DOM query for `[data-fp-target]` (`-mobile` on phones).
    //      This is the canonical anchor — works even if the sidebar
    //      mounted/unmounted/collapsed since the last triggerAnimation.
    //   2. The setFPTargetRef / setMobileTargetRef callback ref —
    //      legacy path, used only as a fallback.
    //   3. Hard-coded screen-edge fallback so the pill never lands at
    //      (0,0). This is what the user was seeing before — the pill
    //      "flying to the right into nothing" because the registered
    //      ref was stale (sidebar collapsed at the time the user
    //      logged in, so fpBoxRef.current was null).
    let targetEl: HTMLElement | null = null;
    if (isMobile) {
      targetEl =
        (document.querySelector('[data-fp-target-mobile="true"]') as HTMLElement | null) ||
        mobileTargetRef ||
        null;
    } else {
      targetEl =
        (document.querySelector('[data-fp-target="true"]') as HTMLElement | null) ||
        fpTargetRef ||
        null;
    }

    let targetX = window.innerWidth - 200;
    let targetY = 300;
    let rect: DOMRect | null = null;
    if (targetEl) {
      rect = targetEl.getBoundingClientRect();
      targetX = rect.left + rect.width / 2;
      targetY = rect.top + rect.height / 2;
    }
    setTarget({ x: targetX, y: targetY, rect });

    const t1 = setTimeout(() => setPhase('travel'), 180);
    const t2 = setTimeout(() => {
      setPhase('arrival');
      // Subtle scale pulse on the target box itself — uses requestAnimationFrame
      // friendly inline transform so we don't fight Tailwind utilities.
      if (targetEl) {
        targetEl.style.transition = 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)';
        targetEl.style.transform = 'scale(1.06)';
        setTimeout(() => {
          targetEl.style.transform = 'scale(1)';
        }, 160);
      }
      if (progressBarRef) {
        progressBarRef.classList.add('animate-pulse');
        setTimeout(() => progressBarRef.classList.remove('animate-pulse'), 520);
      }
    }, 1300);
    const t3 = setTimeout(() => setPhase('done'), 1600);
    const t4 = setTimeout(onComplete, 1800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [fpTargetRef, mobileTargetRef, progressBarRef, onComplete]);

  const isLoss = points < 0;
  const Icon = type === "aura" ? FiZap : FiStar;
  // Loss → red. Aura gain → emerald. FP gain → lime.
  const accentColor = isLoss ? "#FF1744" : type === "aura" ? "#00FF9D" : "#CCFF00";

  // Pill position by phase — start position, then target.
  const pillX = phase === 'birth' ? startX : target.x;
  const pillY = phase === 'birth' ? startY - 4 : target.y;
  const pillScale = phase === 'birth' ? 1 : phase === 'travel' ? 0.85 : 0.5;
  const pillOpacity = phase === 'birth' ? 1 : phase === 'travel' ? 0.95 : 0;

  return (
    <>
      {/* Birth burst — 6 lime particles fan out from the click site.
          Pure CSS via framer-motion; cleared after 480ms so they don't
          accumulate. */}
      <AnimatePresence>
        {phase === 'birth' && burstParticles.map((p, i) => (
          <motion.div
            key={`birth-${i}`}
            className="fixed z-[9998] pointer-events-none rounded-full"
            initial={{
              left: startX,
              top: startY,
              x: -2,
              y: -2,
              opacity: 1,
              scale: 0.8,
            }}
            animate={{
              x: Math.cos(p.angle) * p.distance - 2,
              y: Math.sin(p.angle) * p.distance - 2,
              opacity: 0,
              scale: 0.3,
            }}
            transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
            style={{
              width: 4,
              height: 4,
              backgroundColor: accentColor,
              boxShadow: `0 0 6px ${accentColor}`,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Pill — dark glass, lime accents only. The whole thing
          translates from start to target via a single motion.div with
          a custom cubic-bezier so it feels weighted, not floaty. */}
      <motion.div
        className="fixed z-[9999] pointer-events-none flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] bg-[#0f0f12]/95 backdrop-blur-md border border-white/[0.08] text-white text-sm font-satoshi font-bold shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)]"
        initial={{
          left: startX,
          top: startY - 4,
          opacity: 0,
          scale: 0.4,
          x: '-50%',
          y: '-50%',
        }}
        animate={{
          left: pillX,
          top: pillY,
          opacity: pillOpacity,
          scale: pillScale,
        }}
        transition={
          phase === 'birth'
            ? { type: 'spring', stiffness: 520, damping: 22 }
            : phase === 'travel'
              ? { duration: 1.1, ease: [0.22, 1, 0.36, 1] }
              : { duration: 0.25, ease: 'easeOut' }
        }
      >
        <Icon className="w-3.5 h-3.5" style={{ color: accentColor }} />
        <span style={{ color: accentColor }} className="font-monument tabular-nums">
          {points >= 0 ? "+" : "−"}
          {Math.abs(points) < 1 ? Math.abs(points).toFixed(2) : Math.abs(points).toFixed(0)}
        </span>
        <span className="text-[10px] text-gray-400 font-satoshi font-semibold tracking-wider">
          {type === "aura" ? "AURA" : "FP"}
        </span>
      </motion.div>

      {/* Arrival explosion — 8 particles + ring ripple at target. */}
      <AnimatePresence>
        {phase === 'arrival' && target.rect && arrivalParticles.map((p, i) => (
          <motion.div
            key={`arrival-${i}`}
            className="fixed z-[9998] pointer-events-none rounded-full"
            initial={{
              left: target.x,
              top: target.y,
              x: -2.5,
              y: -2.5,
              opacity: 1,
              scale: 1,
            }}
            animate={{
              x: Math.cos(p.angle) * p.distance - 2.5,
              y: Math.sin(p.angle) * p.distance - 2.5,
              opacity: 0,
              scale: 0.4,
            }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{
              width: 5,
              height: 5,
              backgroundColor: accentColor,
              boxShadow: `0 0 8px ${accentColor}`,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Soft ring ripple at arrival — single expand+fade, no looping
          animate-ping hammering. */}
      <AnimatePresence>
        {phase === 'arrival' && target.rect && (
          <motion.div
            className="fixed z-[9997] pointer-events-none rounded-[8px]"
            initial={{
              left: target.rect.left,
              top: target.rect.top,
              width: target.rect.width,
              height: target.rect.height,
              opacity: 0.6,
              scale: 1,
            }}
            animate={{
              opacity: 0,
              scale: 1.45,
            }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{
              border: `1.5px solid ${accentColor}`,
              boxShadow: `0 0 16px ${accentColor}55`,
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
